import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  addLine,
  cancelSale,
  checkout,
  parkSale,
  removeLine,
  startSale,
  updateLine,
} from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import CartPanel from "@/components/CartPanel";
import ProductGrid from "@/components/ProductGrid";
import PaymentModal from "@/components/PaymentModal";
import TicketPreview from "@/components/TicketPreview";
import CustomerPicker from "@/components/CustomerPicker";
import { toast } from "sonner";

export default function POS() {
  const navigate = useNavigate();
  const { user, session, refreshSession, refreshChain, refreshSage } = useApp();
  const [sale, setSale] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [lastTicket, setLastTicket] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Si no hay sesión de caja → ir a /cash
  useEffect(() => {
    if (!session) {
      navigate("/cash");
    }
  }, [session, navigate]);

  // Iniciar venta vacía al entrar (o si no hay)
  const ensureSale = useCallback(async () => {
    if (sale) return sale;
    if (!session) return null;
    const s = await startSale({}, user, session.id);
    setSale(s);
    return s;
  }, [sale, session, user]);

  useEffect(() => {
    if (!sale && session) ensureSale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const pickProduct = async (p) => {
    const s = await ensureSale();
    if (!s) return;
    const updated = await addLine(s.id, { product_id: p.id, qty: 1 });
    setSale(updated);
  };

  const handleBarcode = async (product) => {
    await pickProduct(product);
    toast.success(`${product.name} añadido`);
  };

  const onChangeQty = async (line, qty) => {
    if (qty <= 0) return onRemove(line);
    const updated = await updateLine(sale.id, line.line_id, { qty });
    setSale(updated);
  };

  const onRemove = async (line) => {
    const updated = await removeLine(sale.id, line.line_id);
    setSale(updated);
  };

  const onClear = async () => {
    if (!sale) return;
    if (!window.confirm("¿Anular el ticket actual?")) return;
    await cancelSale(sale.id);
    setSale(null);
    ensureSale();
  };

  const onPark = async () => {
    if (!sale || !sale.lines.length) return;
    await parkSale(sale.id);
    toast.success("Ticket aparcado");
    setSale(null);
    ensureSale();
  };

  const onPay = () => {
    if (!sale?.lines?.length) return;
    setPayOpen(true);
  };

  const onConfirmPay = async (payments) => {
    try {
      const result = await checkout(sale.id, {
        payments,
        customer_id: sale.customer_id || undefined,
        doc_type: "ticket",
      });
      setLastTicket(result);
      setPayOpen(false);
      setSale(null);
      refreshChain();
      refreshSage();
      toast.success(`Ticket ${result.serie}/${result.numero} cobrado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cobrar");
    }
  };

  const onPickCustomer = async (c) => {
    setPickerOpen(false);
    const s = await ensureSale();
    if (!s) return;
    // Reinicia venta asignando cliente (simple): crea otra borrador con cliente
    if (s.lines.length === 0) {
      const ns = await startSale({ customer_id: c.id }, user, session.id);
      // anular la vacía anterior
      await cancelSale(s.id);
      setSale(ns);
    } else {
      // Si tiene líneas, simplemente actualiza local (el checkout pasará el customer_id)
      setSale({ ...s, customer_id: c.id, customer_name: c.name, customer_nif: c.nif });
    }
  };

  const newTicket = () => {
    setLastTicket(null);
    setSale(null);
    ensureSale();
    refreshSession();
  };

  return (
    <div className="h-full grid grid-cols-1 md:grid-cols-[420px_1fr]">
      <CartPanel
        sale={sale}
        onRemove={onRemove}
        onChangeQty={onChangeQty}
        onClear={onClear}
        onPay={onPay}
        onPark={onPark}
        onSelectCustomer={() => setPickerOpen(true)}
      />
      <ProductGrid onPick={pickProduct} onBarcode={handleBarcode} />

      {payOpen && (
        <PaymentModal
          sale={sale}
          onClose={() => setPayOpen(false)}
          onConfirm={onConfirmPay}
        />
      )}
      {lastTicket && (
        <TicketPreview
          sale={lastTicket}
          onClose={() => setLastTicket(null)}
          onNew={newTicket}
        />
      )}
      {pickerOpen && (
        <CustomerPicker
          currentId={sale?.customer_id}
          onClose={() => setPickerOpen(false)}
          onPick={onPickCustomer}
        />
      )}
    </div>
  );
}
